import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, ArrowRight, Mail, KeyRound, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
      setSuccess('If an account exists, a reset code has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        email,
        otp_code: otpCode,
        new_password: newPassword,
      });
      setSuccess(res.data.message);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const stepConfig = {
    1: { icon: Mail, title: 'Forgot your password?', subtitle: 'Enter your email and we\'ll send you a reset code.' },
    2: { icon: KeyRound, title: 'Enter reset code', subtitle: `We sent a 6-digit code to ${email}` },
    3: { icon: Lock, title: 'Password reset!', subtitle: 'Your password has been changed successfully.' },
  };

  const { icon: StepIcon, title, subtitle } = stepConfig[step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF] px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-[440px]"
      >
        {/* Logo */}
        <Link to="/login" className="flex items-center gap-2.5 mb-10 group w-fit">
          <img src="/logo.svg" alt="PAFHub" className="w-10 h-10" />
          <span className="text-xl font-bold tracking-tight"><span className="text-[#000000]">PAF</span><span className="text-[#4F46E5]">Hub</span></span>
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <StepIcon className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
            </div>
          </div>

          <h2 data-testid="forgot-password-title" className="text-xl font-bold text-[#0F172A] text-center mb-1">
            {title}
          </h2>
          <p className="text-sm text-[#64748B] text-center mb-6">
            {subtitle}
          </p>

          {error && (
            <div data-testid="forgot-password-error" className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">
              {error}
            </div>
          )}

          {success && step !== 3 && (
            <div data-testid="forgot-password-success" className="mb-4 p-3 bg-green-50 border border-green-100 text-green-700 text-sm rounded-xl">
              {success}
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleSendCode}
                className="space-y-4"
              >
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Email address</Label>
                  <Input
                    data-testid="forgot-password-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 h-12 shadow-sm"
                  />
                </div>
                <Button
                  data-testid="forgot-password-send-code-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold text-[15px] shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98] transition-all"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">Send Reset Code <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </motion.form>
            )}

            {step === 2 && (
              <motion.form
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleResetPassword}
                className="space-y-4"
              >
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Reset Code</Label>
                  <Input
                    data-testid="forgot-password-otp-input"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    maxLength={6}
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 h-12 shadow-sm text-center tracking-[0.3em] font-mono text-lg"
                  />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">New Password</Label>
                  <Input
                    data-testid="forgot-password-new-password-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={6}
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 h-12 shadow-sm"
                  />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Confirm Password</Label>
                  <Input
                    data-testid="forgot-password-confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 h-12 shadow-sm"
                  />
                </div>
                <Button
                  data-testid="forgot-password-reset-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold text-[15px] shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98] transition-all"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">Reset Password <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(''); setSuccess(''); }}
                  className="w-full text-sm text-[#64748B] hover:text-indigo-600 transition-colors mt-2"
                >
                  Didn't receive a code? Go back
                </button>
              </motion.form>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center space-y-4"
              >
                <div data-testid="forgot-password-success-msg" className="p-3 bg-green-50 border border-green-100 text-green-700 text-sm rounded-xl">
                  {success}
                </div>
                <Button
                  data-testid="forgot-password-go-login-btn"
                  onClick={() => navigate('/login')}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold text-[15px] shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98] transition-all"
                >
                  <span className="flex items-center gap-2">Back to Sign In <ArrowRight className="w-4 h-4" /></span>
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Back to login link */}
        {step !== 3 && (
          <div className="mt-6 text-center">
            <Link
              data-testid="forgot-password-back-link"
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-indigo-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
